import {Modal, Platform, Pressable, View} from "react-native"
import React, {useMemo} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component"
import BodyPortal from "components/body-portal"
import classNames from "classnames"
import {digs} from "diggerize"
import envSense from "env-sense"
import FontAwesomeIcon from "react-native-vector-icons/FontAwesome"
import memo from "set-state-compare/build/memo"
import {PortalHost} from "conjointment"
import PropTypes from "prop-types"
import useEventListener from "ya-use-event-listener"
import {useModals} from "components/modal"

/**
 * @typedef {object} ComponentsLayoutOverlayProps
 * @property {unknown} children
 * @property {unknown} className
 * @property {unknown} color
 * @property {unknown} dataSet
 * @property {Function} onRequestClose
 * @property {number} zIndex
 */

/** @typedef {object} ComponentsLayoutOverlayState */

/**
 * @extends {ShapeComponent<ComponentsLayoutOverlayProps, ComponentsLayoutOverlayState>}
 */
class ComponentsLayoutOverlay extends ShapeComponent {
  static propTypes = {
    children: PropTypes.node.isRequired,
    onRequestClose: PropTypes.func,
    zIndex: PropTypes.number
  }

  setup() {
    this.modals = useModals()

    if (Platform.OS == "web") {
      useEventListener(window, "keyup", this.tt.onWindowKeyUp)
    }
  }

  render() {
    const {children, className, color, dataSet: givenDataSet, onRequestClose, zIndex, ...restProps} = this.props
    const component = classNames("layout--overlay", givenDataSet?.component)
    const dataSet = useMemo(() => Object.assign({}, givenDataSet, {class: className, component}), [className, component, givenDataSet])
    const amountOfModalsShown = Object.keys(this.tt.modals.modals).length

    // Only way to get the overlay on top of a modal
    if (amountOfModalsShown > 0) {
      return this.renderWithReactNativeModal({dataSet, restProps})
    }

    // Only way to get the menu button on top of an overlay (like the Pro blocker)
    if (Platform.OS == "web") {
      return (
        <BodyPortal>
          {this.modalContent({dataSet, restProps})}
        </BodyPortal>
      )
    }

    return this.modalContent({dataSet, restProps})
  }

  renderWithReactNativeModal({dataSet, restProps}) {
    return (
      <Modal onBackdropPress={this.props.onRequestClose} onRequestClose={this.props.onRequestClose} transparent>
        <PortalHost>
          {this.modalContent({dataSet, restProps})}
        </PortalHost>
      </Modal>
    )
  }

  modalContent({dataSet, restProps}) {
    const {children, color, onRequestClose, zIndex} = this.props
    const {isNative} = envSense()
    let background

    if (color == "blur" && !isNative) {
      background = undefined
    } else if (color == "light") {
      background = "rgba(255, 255, 255, .85)"
    } else {
      background = "rgba(0, 0, 0, .85)"
    }

    return (
      <View
        style={this.cache("overlayViewStyle", {
          position: "fixed",
          top: 0,
          left: 0,
          zIndex: zIndex === undefined ? 997 : zIndex,
          width: "100%",
          height: "100vh",
          background,
          backdropFilter: color == "blur" && !isNative ? "blur(10px)" : undefined,
          color: "#fff"
        }, [background, zIndex])}
      >
        <View dataSet={dataSet} style={this.containerViewStyle ||= {width: "100%", height: "100%"}} {...restProps}>
          <View style={this.childrenViewStyle ||= {alignItems: "center", justifyContent: "center", width: "100%", height: "100%"}}>
            {children}
          </View>
          {onRequestClose &&
            <Pressable
              dataSet={this.requestCloseButtonDataSet ||= {class: "close-overlay-button"}}
              onPress={onRequestClose}
              style={this.requestCloseButtonStyle ||= {
                position: "absolute",
                top: 30,
                right: 30
              }}
            >
              <FontAwesomeIcon
                name="remove"
                style={this.cache("iconStyle", {
                  color: color == "light" ? "#000" : "#fff",
                  fontSize: 36
                }, [color])}
              />
            </Pressable>
          }
        </View>
      </View>
    )
  }

  onWindowKeyUp = (e) => {
    const {onRequestClose} = this.props
    const {keyCode} = digs(e, "keyCode")

    if (keyCode == 27 && onRequestClose) { // Escape key
      e.preventDefault()
      onRequestClose()
    }
  }
}

export default memo(shapeComponent(ComponentsLayoutOverlay))
